import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, FileSpreadsheet } from 'lucide-react';
import { api, fmt } from '../lib/api';
import LoadingSpinner from '../components/LoadingSpinner';
import DataTable, { type Column } from '../components/DataTable';
import type { Row } from '../lib/api';

export default function BatchDetail() {
  const { sys: sysSlug, batchId } = useParams<{ sys: string; batchId: string }>();

  const summaryQuery = useQuery({
    queryKey: ['batch', batchId],
    queryFn: () => api.getBatch(batchId!),
    enabled: !!batchId,
  });

  const rowsQuery = useQuery({
    queryKey: ['batch-rows', batchId],
    queryFn: () => api.rowsByBatch(batchId!),
    enabled: !!batchId,
  });

  if (summaryQuery.isLoading || rowsQuery.isLoading) {
    return <LoadingSpinner label="Loading batch…" />;
  }
  if (summaryQuery.isError) return <div className="alert alert-error"><p>{(summaryQuery.error as Error).message}</p></div>;

  const data = summaryQuery.data!;
  const rows = rowsQuery.data?.rows ?? [];

  const columns: Column<Row>[] = [
    { key: 'carrier', header: 'Carrier', sortable: true, filterable: true, width: '8rem' },
    { key: 'policy_id', header: 'Policy', sortable: true, filterable: true, width: '12rem' },
    { key: 'policyholder_name', header: 'Policyholder', sortable: true, filterable: true },
    { key: 'writing_agent_id', header: 'Writing ID', sortable: true, filterable: true, width: '8rem' },
    { key: 'agent_name', header: 'Agent', sortable: true, filterable: true, width: '8rem' },
    {
      key: 'raw_amount',
      header: 'Raw',
      sortable: true,
      align: 'right',
      width: '6rem',
      render: (r) => <span className="font-mono">{fmt.money(r.raw_amount)}</span>,
    },
    {
      key: 'override_amount',
      header: 'Override',
      sortable: true,
      align: 'right',
      width: '6rem',
      render: (r) => (
        <span
          className="font-mono text-xs"
          style={{ color: 'var(--color-text-muted)' }}
          title={r.override_formula}
        >
          {fmt.money(r.override_amount)}
        </span>
      ),
    },
    {
      key: 'net_amount',
      header: 'Net',
      sortable: true,
      align: 'right',
      width: '6rem',
      render: (r) => <span className="font-mono font-semibold">{fmt.money(r.net_amount)}</span>,
    },
    {
      key: 'transaction_date',
      header: 'Date',
      sortable: true,
      width: '8rem',
      render: (r) => <span className="text-xs">{fmt.dateOnly(r.transaction_date)}</span>,
    },
  ];

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center gap-3">
        <Link
          to={`/${sysSlug}/imports`}
          className="btn-icon"
          style={{ color: 'var(--color-text-muted)' }}
          aria-label="Back to imports"
        >
          <ArrowLeft size={20} />
        </Link>
        <div>
          <h2 className="text-xl font-semibold" style={{ color: 'var(--color-text)' }}>
            <FileSpreadsheet
              size={18}
              className="inline mr-2"
              style={{ color: 'var(--color-primary)' }}
            />
            {data.batch.original_filename ?? data.batch.batch_id}
          </h2>
          <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
            Batch <span className="font-mono">{data.batch.batch_id}</span> · {fmt.date(data.batch.created_at)}
          </p>
          {data.batch.extra_meta?.duplicate_warning && (
            <span className="text-xs px-2 py-0.5 rounded mt-1 inline-block" style={{ backgroundColor: 'rgba(245,158,11,0.15)', color: 'rgb(245,158,11)' }}>
              Possible re-upload — {data.batch.extra_meta.duplicate_warning.overlap_pct}% of rows already imported
            </span>
          )}
          {data.batch.extra_meta?.zero_rows_warning && (
            <span className="text-xs px-2 py-0.5 rounded mt-1 inline-block" style={{ backgroundColor: 'rgba(245,158,11,0.15)', color: 'rgb(245,158,11)' }}>
              0 rows imported but statement total ${data.batch.extra_meta.zero_rows_warning.printed_total} — check the file
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SummaryCard label="Rows" value={data.summary.total_rows.toLocaleString()} />
        <SummaryCard label="Raw total" value={fmt.money(data.summary.total_raw)} />
        <SummaryCard label="Net to agents" value={fmt.money(data.summary.total_net)} accent="primary" />
      </div>

      {Object.keys(data.carriers).length > 0 && (
        <div className="card">
          <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--color-text)' }}>
            Per-carrier
          </h3>
          <table className="table">
            <thead>
              <tr>
                <th>Carrier</th>
                <th className="text-right">Rows</th>
                <th className="text-right">Raw</th>
                <th className="text-right">Net</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(data.carriers).map(([carrier, c]) => (
                <tr key={carrier}>
                  <td>{carrier}</td>
                  <td className="text-right font-mono">{c.rows}</td>
                  <td className="text-right font-mono">{fmt.money(c.raw)}</td>
                  <td className="text-right font-mono font-semibold">{fmt.money(c.net)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="card overflow-hidden p-0">
        <DataTable columns={columns} data={rows} emptyMessage="No rows in this batch yet." />
      </div>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: 'primary' | 'success' | 'danger';
}) {
  const cls =
    accent === 'success'
      ? 'metric-card metric-card-success'
      : accent === 'danger'
        ? 'metric-card metric-card-danger'
        : 'metric-card metric-card-primary';
  return (
    <div className={cls}>
      <p
        className="text-xs uppercase tracking-wider mb-1"
        style={{ color: 'var(--color-text-muted)' }}
      >
        {label}
      </p>
      <p className="text-2xl font-bold" style={{ color: 'var(--color-text)' }}>
        {value}
      </p>
    </div>
  );
}
